import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, Euro, AlertTriangle, Clock, Send, History, Ban
} from "lucide-react";
import api from "@/api";
import { usePagination } from "@/lib/usePagination";

const fmt = (v) => (v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("it-IT"); } catch { return d; }
};

const LIVELLI = {
  nessuno: { label: "In scadenza", cls: "bg-gray-100 text-gray-600", color: "text-gray-500" },
  primo:   { label: "1° sollecito", cls: "bg-yellow-100 text-yellow-700", color: "text-yellow-600" },
  secondo: { label: "2° sollecito", cls: "bg-orange-100 text-orange-700", color: "text-orange-600" },
  terzo:   { label: "3° sollecito", cls: "bg-red-100 text-red-700", color: "text-red-600" },
};

export default function SollecitiTab({ currentOperator }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [management, setManagement] = useState("agenzia");

  // Send dialog
  const [sendDialog, setSendDialog] = useState(null); // invoice object
  const [sendNote, setSendNote] = useState("");
  const [sending, setSending] = useState(false);

  // History dialog
  const [historyDialog, setHistoryDialog] = useState(null); // invoice id
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSolleciti(
        year !== "all" ? year : null,
        management !== "all" ? management : null
      );
      setData(res);
    } catch (e) {
      console.error("Solleciti load error:", e);
    } finally {
      setLoading(false);
    }
  }, [year, management]);

  useEffect(() => { load(); }, [load]);

  // Paginazione (20/02/2026).
  const invoices = data?.invoices || [];
  const {
    paged: pagedInvoices,
    paginationBar: solPaginationBar,
  } = usePagination(invoices, {
    storageKey: "solleciti",
    itemLabelSingular: "fattura",
    itemLabelPlural: "fatture",
  });

  // Send reminder
  const handleSend = async () => {
    if (!sendDialog) return;
    setSending(true);
    const liv = LIVELLI[sendDialog.livello] || LIVELLI.primo;
    try {
      await api.sendReminder(sendDialog.id, {
        tipo: liv.label,
        operatore: currentOperator?.fullName || currentOperator?.name || "",
        note: sendNote,
      });
      setSendDialog(null);
      setSendNote("");
      load(); // Refresh
    } catch (e) {
      console.error("Send error:", e);
    } finally {
      setSending(false);
    }
  };

  // Load history
  const openHistory = async (invoiceId) => {
    setHistoryDialog(invoiceId);
    setHistoryLoading(true);
    try {
      const h = await api.getReminderHistory(invoiceId);
      setHistoryData(h);
    } catch { setHistoryData([]); }
    finally { setHistoryLoading(false); }
  };

  const s = data?.summary;

  return (
    <div className="space-y-4" data-testid="solleciti-tab">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-gray-800">Solleciti Incassi</h3>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-[100px] h-8 text-sm" data-testid="solleciti-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={management} onValueChange={setManagement}>
          <SelectTrigger className="w-[120px] h-8 text-sm" data-testid="solleciti-mgmt"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte</SelectItem>
            <SelectItem value="studio">Studio</SelectItem>
            <SelectItem value="agenzia">Agenzia</SelectItem>
          </SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
      </div>

      {/* Summary cards */}
      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-blue-400" data-testid="card-da-incassare">
            <CardContent className="p-3">
              <p className="text-[10px] text-gray-500 uppercase">Da Incassare</p>
              <p className="text-xl font-bold text-gray-900">{fmt(s.totaleDaIncassare)} &euro;</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400" data-testid="card-scaduto">
            <CardContent className="p-3">
              <p className="text-[10px] text-gray-500 uppercase">Totale Scaduto</p>
              <p className="text-xl font-bold text-red-700">{fmt(s.totaleScaduto)} &euro;</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-400" data-testid="card-count-scadute">
            <CardContent className="p-3">
              <p className="text-[10px] text-gray-500 uppercase">Fatture Scadute</p>
              <p className="text-xl font-bold text-gray-900">{s.countScadute}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-600" data-testid="card-count-critiche">
            <CardContent className="p-3">
              <p className="text-[10px] text-gray-500 uppercase">Critiche (&gt;30gg)</p>
              <p className="text-xl font-bold text-red-700">{s.countCritiche}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Cliente</TableHead>
              <TableHead className="w-[90px]">N. Fattura</TableHead>
              <TableHead className="w-[85px]">Data Fatt.</TableHead>
              <TableHead className="w-[85px]">Scadenza</TableHead>
              <TableHead className="w-[65px] text-center">Ritardo</TableHead>
              <TableHead className="w-[100px] text-right">Residuo</TableHead>
              <TableHead className="w-[110px]">Stato</TableHead>
              <TableHead className="w-[130px]">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">Nessuna fattura con residuo da incassare</TableCell></TableRow>
            ) : pagedInvoices.map((inv, i) => {
              const lv = LIVELLI[inv.livello] || LIVELLI.nessuno;
              return (
                <TableRow key={inv.id} className="hover:bg-gray-50/50" data-testid={`sollecito-row-${i}`}>
                  <TableCell className="text-sm font-medium">{inv.clientName}</TableCell>
                  <TableCell className="font-mono text-sm">{inv.invoiceNumber}</TableCell>
                  <TableCell className="text-sm">{fmtDate(inv.invoiceDate)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(inv.dueDate)}</TableCell>
                  <TableCell className="text-center">
                    <span className={`text-sm font-bold ${lv.color}`}>
                      {inv.giorniRitardo > 0 ? `${inv.giorniRitardo}gg` : "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-right font-medium text-amber-700">{fmt(inv.residuo)} &euro;</TableCell>
                  <TableCell>
                    <Badge className={`${lv.cls} text-xs`}>{lv.label}</Badge>
                    {inv.reminderCount > 0 && (
                      <span className="ml-1 text-[10px] text-gray-400">({inv.reminderCount}x)</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => { setSendDialog(inv); setSendNote(""); }} data-testid={`send-btn-${i}`}>
                        <Send className="h-3 w-3 mr-1" /> Sollecita
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openHistory(inv.id)} data-testid={`history-btn-${i}`}>
                        <History className="h-3.5 w-3.5 text-gray-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {solPaginationBar}
      </Card>

      {/* Send Reminder Dialog */}
      <Dialog open={!!sendDialog} onOpenChange={() => setSendDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="send-reminder-dialog">
          <DialogHeader>
            <DialogTitle>Invia Sollecito</DialogTitle>
          </DialogHeader>
          {sendDialog && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <p><b>Cliente:</b> {sendDialog.clientName}</p>
                <p><b>Fattura:</b> {sendDialog.invoiceNumber} del {fmtDate(sendDialog.invoiceDate)}</p>
                <p><b>Importo residuo:</b> <span className="text-amber-700 font-bold">{fmt(sendDialog.residuo)} &euro;</span></p>
                <p><b>Giorni ritardo:</b> <span className={(LIVELLI[sendDialog.livello] || LIVELLI.nessuno).color + " font-bold"}>{sendDialog.giorniRitardo}gg</span></p>
                {sendDialog.clientEmail && <p><b>Email:</b> {sendDialog.clientEmail}</p>}
                {sendDialog.clientPhone && <p><b>Tel:</b> {sendDialog.clientPhone}</p>}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-blue-800 mb-1">Testo sollecito suggerito:</p>
                <p className="text-blue-700 text-xs leading-relaxed">
                  Gentile {sendDialog.clientName}, le ricordiamo che la fattura n. {sendDialog.invoiceNumber} del {fmtDate(sendDialog.invoiceDate)} 
                  {" "}presenta un importo residuo di {fmt(sendDialog.residuo)} &euro; con scadenza {fmtDate(sendDialog.dueDate)}.
                  {" "}La preghiamo di provvedere al pagamento quanto prima. Cordiali saluti.
                </p>
              </div>

              <div>
                <Label className="text-sm">Note aggiuntive</Label>
                <Textarea value={sendNote} onChange={e => setSendNote(e.target.value)} placeholder="Note interne sul sollecito..." rows={2} data-testid="reminder-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)}>Annulla</Button>
            <Button onClick={handleSend} disabled={sending} data-testid="confirm-send-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Registra Sollecito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={() => setHistoryDialog(null)}>
        <DialogContent className="max-w-md" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle>Storico Solleciti</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : historyData.length === 0 ? (
            <p className="text-center text-gray-400 py-6">Nessun sollecito registrato</p>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {historyData.map((h, i) => (
                <div key={h.id || i} className="border rounded-lg p-3 text-sm" data-testid={`history-entry-${i}`}>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">{h.tipo}</Badge>
                    <span className="text-xs text-gray-400">{fmtDate(h.data)}</span>
                  </div>
                  {h.operatore && <p className="text-xs text-gray-500 mt-1">Operatore: {h.operatore}</p>}
                  {h.note && <p className="text-xs text-gray-600 mt-1">{h.note}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
